import type { MutableRefObject } from 'react'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useError } from '../useError'
import type { ConfirmDialogOptions } from '../useConfirmDialog'
import { useConfirmDialog } from '../useConfirmDialog'

export type InlineEditorOnClick = () => void
export type UseInlineEditorOptions = {
  saveOnEnterKey?: boolean
  confirmBeforeSaveOptions?: ConfirmDialogOptions
}
export type UseInlineEditorUpdate = (value: any, confirmResult?: any) => Promise<void>

export const useInlineEditor = (
  initialValue = '',
  update: UseInlineEditorUpdate,
  inputRef: MutableRefObject<any>,
  { saveOnEnterKey = true, confirmBeforeSaveOptions }: UseInlineEditorOptions = {}
) => {
  const [editable, setEditable] = useState(false)
  const [saving, setSaving] = useState(false)
  const [newValue, setNewValue] = useState(initialValue)
  const { onError, error, setError } = useError()
  useEffect(() => setNewValue(initialValue), [initialValue])

  const { confirm } = useConfirmDialog(confirmBeforeSaveOptions)

  useEffect(() => {
    if (editable) focus()
  }, [editable])

  const focus = () => {
    inputRef && inputRef.current && inputRef.current.focus()
    inputRef && inputRef.current && inputRef.current.select()
  }
  const onClick: InlineEditorOnClick = () => {
    setEditable(true)
  }

  const _save = async (args?: any) => {
    try {
      setError(null)
      setSaving(true)
      await update(newValue, args)
      setEditable(false)
    } catch (e) {
      onError(e)
      setEditable(true)
      //focus()
    } finally {
      setSaving(false)
    }
  }

  const saveNow = async (args?: any) => {
    //if there is a value for confirmBeforeSaveOptions, it means we want the confirmation dialog to appear first

    if (confirmBeforeSaveOptions) {
      // Forward the dialog's resolved value (e.g. NO=false / YES=true) into _save so it
      // reaches updateParams. An explicit `args` still wins; no caller passes one today.
      return confirm((confirmResult) => _save(args ?? confirmResult), cancelNow, confirmBeforeSaveOptions)
    } else {
      return _save(args)
    }
  }
  const cancelNow = async () => {
    setError(null)
    setNewValue(initialValue)
    setEditable(false)
  }
  const onBlur = async () => {
    return saveNow()
  }
  const onKeyDown = async (e: React.KeyboardEvent<any>) => {
    if (e.key === 'Escape') {
      cancelNow()
    } else if (e.key === 'Enter' && saveOnEnterKey) {
      //if there is shift key, it means the user is trying to create a new line, so just skip it
      if (e.shiftKey) {
        return
      }

      //the default enter behavior does not work well with "ConfirmDialog" so we handle it when confirm is available
      if (confirmBeforeSaveOptions) {
        e.preventDefault()
      }
      return saveNow()
    }
  }

  return {
    editable,
    setEditable,
    saving,
    error,
    newValue,
    setNewValue,
    onClick,
    onBlur,
    onKeyDown,
    focus,
    inputRef,
    saveNow,
    cancelNow,
  }
}
